import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	Radio,
	RadioGroup,
	Step,
	StepConnector,
	StepLabel,
	Stepper,
	TextField,
	Typography
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import * as Clipboard from "clipboard-polyfill";
import { Controller, useForm } from "react-hook-form";
import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import {
	IAddCameraRequestData,
	ICameraCanvasUpdatedDetails,
	ICameraDetails,
	ICanvasComponentRef,
	IGetCameraStatusRequestData,
	IUpdateCameraDetails
} from "./interfaces";
import {
	useGetCamerasListQuery,
	useGetSiteConfigQuery,
	useGetSocietyConfigQuery,
	useLazyGetCameraStatusQuery
} from "../../redux/reducers/settings.reducer";
import CanvasComponent from "./CameraCanvas";
import { useAppDispatch, useAppSelector } from "../../redux";
import NumberInput from "../../common/NumberInput";
import { showFeedbackNotification } from "../../redux/reducers/notifications.reducer";
import { getRestrictedAccessList, RESTRICTED_ACCESS_E } from "../../utils/commonUtils";
import { CAMERA_SETUP_STEPS, DEFAULT_NUMBER_OF_LINES, RTSP_REGEX, URL_REGEX } from "./constants";
import { useAddCameraMutation, useUpdateCameraDetailsMutation } from "../../redux/reducers/configuration.reducer";
import { maxBy } from "lodash";
import ConfirmationDialog from "../ConfirmationDialog";
import { CAMERA_STANDARD_FPS_RANGE, CAMERA_STANDARD_RESOLUTION } from "../../utils/constants";
import { IGetRTSPImageResponseData } from "../../interfaces";

interface ICameraSetupDialogProps {
	open: boolean;
	cameraDetails: ICameraDetails | null;
	imageDetails?: IGetRTSPImageResponseData;
	imageRTSP?: string;
	onClose: () => void;
}

interface ICameraFormDetails {
	camera_name: string;
	rtsp_url: string;
	camera_settings_url: string;
	site_name: string;
	site_id: string;
	camera_id: string;
	camera_location: string;
	secondary_rtsp_url: string;
	tcp_socket_host: string;
	tcp_socket_port: string;
	drop_frame: string;
}

const CameraSetupDialog: FC<ICameraSetupDialogProps> = (props) => {
	const { open, cameraDetails, imageDetails, imageRTSP, onClose } = props;

	const dispatch = useAppDispatch();

	const authState = useAppSelector((state) => state.auth);

	const canvasRef = useRef<ICanvasComponentRef>(null);
	const getCameraImageAbort = useRef<(() => void) | null>(null);

	const [currentStep, setCurrentStep] = useState<number>(0);
	const [crossingLinesGeneration, setCrossingLinesGeneration] = useState<"auto" | "manual">("auto");
	const [numberOfLines, setNumberOfLines] = useState<string>(String(DEFAULT_NUMBER_OF_LINES));
	const [showCanvasPreview, setShowCanvasPreview] = useState<boolean>(false);
	const [showPreviewLoading, setShowPreviewLoader] = useState<boolean>(false);
	const [showSubmitButton, setShowSubmitButton] = useState<boolean>(false);
	const [showCopyRtspButton, setShowCopyRtspButton] = useState<{ [key in "rtsp_url" | "secondary_rtsp_url"]: boolean }>(
		{
			rtsp_url: true,
			secondary_rtsp_url: true
		}
	);
	const [showReadOnlyPreview, setShowReadOnlyPreview] = useState<boolean>(false);
	const [showResolutionConfirmation, setShowResolutionConfirmation] = useState<boolean>(false);
	const [showFPSConfirmation, setShowFPSConfirmation] = useState<boolean>(false);

	// APIS
	const { data: getCamerasListResponse, refetch: refetchCamerasList } = useGetCamerasListQuery({});
	const camerasList = useMemo<ICameraDetails[]>(
		() => getCamerasListResponse?.results ?? [],
		[getCamerasListResponse?.results]
	);

	const { data: getSocietyConfigResponse } = useGetSocietyConfigQuery();
	const societyConfig = getSocietyConfigResponse?.results ?? [];

	const { data: siteConfig } = useGetSiteConfigQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	const [
		addCamera,
		{ isLoading: addCameraLoading, isSuccess: addCameraSuccess, error: addCameraError, reset: resetAddCameraState }
	] = useAddCameraMutation();

	const [
		getCameraImage,
		{
			data: cameraImageData,
			isLoading: getCameraImageLoading,
			isFetching: getCameraImageFetching,
			isSuccess: getCameraImageSuccess,
			error: getCameraImageError
		}
	] = useLazyGetCameraStatusQuery();

	const [
		updateCameraDetails,
		{
			data: updateCameraResponse,
			isLoading: updateCameraLoading,
			isSuccess: updateCameraSuccess,
			error: updateCameraError,
			reset: resetUpdateCameraDetailsState
		}
	] = useUpdateCameraDetailsMutation();

	const RestrictedAccess = useMemo<RESTRICTED_ACCESS_E[]>(
		() => getRestrictedAccessList(siteConfig?.cloud_sync_enabled ?? true),
		[siteConfig]
	);

	const {
		reset,
		watch,
		control,
		setError,
		register,
		getValues,
		trigger,
		formState: { errors }
	} = useForm<ICameraFormDetails>();

	const rtspUrlInput = watch("rtsp_url");

	function handleFetchSampleButtonClick() {
		const formData = getValues();

		if (!formData.rtsp_url) {
			setError("rtsp_url", { type: "required", message: "RTSP is required" });
			return;
		}

		if (camerasList && camerasList.length > 0) {
			const existingURLCamera = camerasList.find((cameraItem) => cameraItem.rtsp_uri === formData.rtsp_url);
			if (existingURLCamera && existingURLCamera.id !== cameraDetails?.id) {
				dispatch(showFeedbackNotification({ message: "Entered RTSP URL already exists", severity: "error" }));
				return;
			}

			const existingSiteIdCamera = camerasList.find((cameraItem) => cameraItem.site_id === Number(formData.site_id));

			if (
				existingSiteIdCamera &&
				existingSiteIdCamera.id !== cameraDetails?.id &&
				existingSiteIdCamera.is_entry !== Boolean(Number(formData.camera_location))
			) {
				dispatch(
					showFeedbackNotification({
						message: `A camera with same site ID already exists for ${
							existingSiteIdCamera.is_entry ? "entry" : "exit"
						}`,
						severity: "error"
					})
				);

				return;
			}

			const existingSourceIdCamera = camerasList.find(
				(cameraItem) => Number(cameraItem.camera_id) === Number(formData.camera_id)
			);
			if (existingSourceIdCamera && existingSourceIdCamera.id !== cameraDetails?.id) {
				dispatch(showFeedbackNotification({ message: "Entered Camera ID already exists", severity: "error" }));
				return;
			}
		}

		const requestData: IGetCameraStatusRequestData = {
			primary: {
				rtsp_uri: formData.rtsp_url,
				fetch_frame: true
			}
		};

		if (formData.secondary_rtsp_url) {
			requestData.secondary = {
				rtsp_uri: formData.secondary_rtsp_url,
				fetch_frame: false
			};
		}

		const { abort } = getCameraImage(requestData);
		getCameraImageAbort.current = abort;
	}

	function handleChangeCrossingLinesGeneration(updatedValue: "auto" | "manual") {
		setCrossingLinesGeneration(updatedValue);
		setShowSubmitButton(false);
		if (updatedValue === "auto") setNumberOfLines(String(DEFAULT_NUMBER_OF_LINES));
	}

	function handleChangeNumberOfLines(updatedValue: string) {
		setNumberOfLines(updatedValue);
		setShowSubmitButton(false);
	}

	function handleNextButtonClick() {
		switch (currentStep) {
			case 0:
				trigger().then((isFormValid) => {
					if (isFormValid) {
						const saveButtonDisabled = isSaveButtonDisabled();
						if (imageDetails && imageDetails.image && !saveButtonDisabled && imageRTSP === rtspUrlInput) {
							setCurrentStep(1);
						} else {
							handleFetchSampleButtonClick();
						}
					}
				});
				break;

			case 1:
				if (canvasRef.current) canvasRef.current.changeCanvasStep(2);
				break;

			case 2:
				if (canvasRef.current) canvasRef.current.changeCanvasStep(3);
				break;

			case 3:
				break;

			default:
				break;
		}
	}

	function handleBackButtonClick() {
		// if (canvasRef.current) canvasRef.current.changeCanvasStep(currentStep - 1);

		switch (currentStep) {
			case 0:
				break;

			case 1:
				setCurrentStep(0);
				break;

			case 2:
				if (canvasRef.current) canvasRef.current.changeCanvasStep(1);
				break;

			case 3:
				if (canvasRef.current) canvasRef.current.editCanvas();
				if (canvasRef.current) canvasRef.current.changeCanvasStep(2);
				break;

			default:
				break;
		}
	}

	function handleUndoButtonClick() {
		if (canvasRef.current) canvasRef.current.undoCanvasDrawing();
	}

	function handleResetButtonClick() {
		if (canvasRef.current) canvasRef.current.resetCanvas();
		setNumberOfLines(String(DEFAULT_NUMBER_OF_LINES));
		setCrossingLinesGeneration("auto");
	}

	function handleEditButtonClick() {
		if (canvasRef.current) canvasRef.current.editCanvas();
	}

	function handlePreviewButtonClick() {
		if (canvasRef.current) canvasRef.current.previewCameraCanvas();
	}

	function handleCopyRTSP(key: "rtsp_url" | "secondary_rtsp_url") {
		const formData = getValues();
		// navigator.clipboard.writeText(formData.rtsp_url);

		Clipboard.writeText(formData[key])
			.then(() => {
				setShowCopyRtspButton((currentShowCopyButtonState) => ({
					...currentShowCopyButtonState,
					[key]: false
				}));

				setTimeout(() => {
					setShowCopyRtspButton((currentShowCopyButtonState) => ({
						...currentShowCopyButtonState,
						[key]: true
					}));
				}, 1000);
			})
			.catch((error) => console.error("UNABLE TO COPY RTSP URL ::", error));
	}

	function isNextButtonLoading(): boolean {
		switch (currentStep) {
			case 0:
				return getCameraImageLoading || getCameraImageFetching;

			default:
				return showPreviewLoading;
		}
	}

	function handleSubmitCameraSetupDetails(cameraSetupDetails?: ICameraCanvasUpdatedDetails) {
		const data = getValues();

		if (camerasList && camerasList.length > 0) {
			const existingSiteIdCamera = camerasList.find(
				(cameraItem) => Number(cameraItem.site_id) === Number(data.site_id)
			);

			if (
				existingSiteIdCamera &&
				existingSiteIdCamera.id !== cameraDetails?.id &&
				existingSiteIdCamera.is_entry !== Boolean(Number(data.camera_location))
			) {
				dispatch(
					showFeedbackNotification({
						message: `A camera with same site ID already exists for ${
							existingSiteIdCamera.is_entry ? "entry" : "exit"
						}`,
						severity: "error"
					})
				);

				return;
			}

			const existingSourceIdCamera = camerasList.find(
				(cameraItem) => Number(cameraItem.camera_id) === Number(data.camera_id)
			);
			if (existingSourceIdCamera && existingSourceIdCamera.id !== cameraDetails?.id) {
				dispatch(showFeedbackNotification({ message: "Entered Camera ID already exists", severity: "error" }));
				return;
			}
		}

		if (cameraDetails) {
			const updatedCameraDetails: IUpdateCameraDetails = {
				rtsp_uri: data.rtsp_url,
				// camera_site: data.camera_name,
				is_entry: Boolean(Number(data.camera_location)),
				site_id: data.site_id,
				camera_id: data.camera_id,
				site_name: data.camera_name,
				camera_page_url: data.camera_settings_url,
				secondary_stream_rtsp_uri: data.secondary_rtsp_url ? data.secondary_rtsp_url : null,
				tcp_socket_host: data.tcp_socket_host || null,
				tcp_socket_port: data.tcp_socket_port || null,
				drop_frame: Number(data.drop_frame)
			};

			if (cameraSetupDetails) {
				updatedCameraDetails.roi = cameraSetupDetails.roi;
				updatedCameraDetails.line = cameraSetupDetails.line;
				updatedCameraDetails.height =
					cameraImageData?.primary?.image_data?.height ?? imageDetails?.height ?? cameraSetupDetails.height;
				updatedCameraDetails.width =
					cameraImageData?.primary?.image_data?.width ?? imageDetails?.width ?? cameraSetupDetails.width;
				updatedCameraDetails.num_lines = cameraSetupDetails.numberOfLines;
			}

			updateCameraDetails({
				id: cameraDetails.id,
				data: updatedCameraDetails
			});
		} else {
			const addCameraDetails: IAddCameraRequestData = {
				rtsp_uri: data.rtsp_url,
				// camera_site: data.camera_name,
				is_entry: Boolean(Number(data.camera_location)),
				roi: "",
				line: "",
				society: societyConfig && societyConfig.length > 0 ? societyConfig[0].id : 1,
				width: cameraImageData?.primary?.image_data?.width ?? imageDetails?.width ?? 0,
				height: cameraImageData?.primary?.image_data?.height ?? imageDetails?.height ?? 0,
				site_id: Number(data.site_id),
				camera_id: Number(data.camera_id),
				site_name: data.camera_name,
				camera_page_url: data.camera_settings_url,
				secondary_stream_rtsp_uri: data.secondary_rtsp_url || null,
				tcp_socket_host: data.tcp_socket_host || null,
				tcp_socket_port: data.tcp_socket_port || null,
				drop_frame: Number(data.drop_frame)
			};

			if (!cameraSetupDetails) {
				dispatch(showFeedbackNotification({ message: "Camera Setup not completed", severity: "error" }));
				return;
			}

			addCameraDetails.roi = cameraSetupDetails.roi;
			addCameraDetails.line = cameraSetupDetails.line;
			addCameraDetails.width =
				cameraImageData?.primary?.image_data?.width ?? imageDetails?.width ?? cameraSetupDetails.width;
			addCameraDetails.height =
				cameraImageData?.primary?.image_data?.height ?? imageDetails?.height ?? cameraSetupDetails.height;

			addCamera(addCameraDetails);
		}
	}

	function handleSaveButtonClick() {
		if (currentStep === 0) {
			trigger().then((isFormValid) => {
				if (isFormValid) handleSubmitCameraSetupDetails();
			});
		} else if (canvasRef.current) canvasRef.current.submitCameraCanvas();
	}

	function isSaveButtonDisabled() {
		if (isNextButtonLoading()) return true;

		if (cameraDetails && rtspUrlInput !== cameraDetails.rtsp_uri) return true;

		return false;
	}

	function handleCameraLocationChange() {
		const siteId = getValues("site_id");
		if (siteId) trigger("site_id");
	}

	function handleShowCameraDetails() {
		setShowReadOnlyPreview(false);
		setCurrentStep(0);
	}

	function handleOpenFPSConfirmationDialog() {
		setShowFPSConfirmation(true);
	}

	function handleCloseFPSConfirmationDialog() {
		setShowFPSConfirmation(false);
	}

	function handleConfirmCameraFPS() {
		handleCloseFPSConfirmationDialog();
		setCurrentStep(1);
	}

	function handleOpenResolutionConfirmationDialog() {
		setShowResolutionConfirmation(true);
	}

	function handleCloseResolutionConfirmationDialog() {
		setShowResolutionConfirmation(false);
	}

	function handleConfirmCameraResolution() {
		if (cameraImageData && cameraImageData.primary && cameraImageData.primary.image_data) {
			handleCloseResolutionConfirmationDialog();

			if (
				cameraImageData.primary.image_data.fps < CAMERA_STANDARD_FPS_RANGE.min ||
				cameraImageData.primary.image_data.fps > CAMERA_STANDARD_FPS_RANGE.max
			) {
				handleOpenFPSConfirmationDialog();
			} else {
				setCurrentStep(1);
			}
		}
	}

	useEffect(() => {
		if (open) {
			if (cameraDetails) {
				if (cameraDetails.num_lines) {
					setNumberOfLines(String(cameraDetails.num_lines));
					if (cameraDetails.num_lines !== DEFAULT_NUMBER_OF_LINES) setCrossingLinesGeneration("manual");
				}

				// setCurrentStep(3);
				// getCameraSampleImage(cameraDetails.rtsp_uri);

				reset({
					camera_name: cameraDetails.site_name,
					rtsp_url: cameraDetails.rtsp_uri,
					camera_settings_url: cameraDetails.camera_page_url ?? "",
					site_name: cameraDetails.site_name,
					site_id: String(cameraDetails.site_id),
					camera_id: String(cameraDetails.camera_id),
					camera_location: String(Number(cameraDetails.is_entry)),
					secondary_rtsp_url: cameraDetails.secondary_stream_rtsp_uri,
					tcp_socket_host: cameraDetails.tcp_socket_host ?? "",
					tcp_socket_port: cameraDetails.tcp_socket_port ?? "",
					drop_frame: cameraDetails.drop_frame ? String(cameraDetails.drop_frame) : "0"
				});
			} else {
				let siteId = "1";
				let cameraId = "1";

				const maxSiteIdDetails = maxBy(camerasList, (item) => Number(item.site_id));
				const maxCameraIdDetails = maxBy(camerasList, (item) => Number(item.camera_id));

				if (maxSiteIdDetails) siteId = String(Number(maxSiteIdDetails.site_id) + 1);
				if (maxCameraIdDetails) cameraId = String(Number(maxCameraIdDetails.camera_id) + 1);

				reset({
					camera_name: "",
					rtsp_url: "",
					camera_settings_url: "",
					site_name: "",
					site_id: siteId,
					camera_id: cameraId,
					camera_location: "1",
					secondary_rtsp_url: "",
					tcp_socket_host: "",
					tcp_socket_port: "",
					drop_frame: "0"
				});
			}
		} else {
			setCurrentStep(0);
			setShowReadOnlyPreview(false);

			if (getCameraImageAbort.current) {
				getCameraImageAbort.current();
			}
		}
	}, [cameraDetails, camerasList, open, reset]);

	useEffect(() => {
		if (getCameraImageSuccess && cameraImageData) {
			if (cameraImageData.primary && cameraImageData.primary.status && cameraImageData.primary.image_data) {
				if (RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG)) {
					setShowReadOnlyPreview(true);
				} else {
					if (cameraImageData.secondary && (!cameraImageData.secondary || !cameraImageData.secondary.status)) {
						dispatch(
							showFeedbackNotification({
								message: "Secondary RTSP camera not found",
								severity: "error"
							})
						);
					}

					if (
						cameraImageData.primary.image_data.width !== CAMERA_STANDARD_RESOLUTION.width ||
						cameraImageData.primary.image_data.height !== CAMERA_STANDARD_RESOLUTION.height
					) {
						handleOpenResolutionConfirmationDialog();
					} else if (
						cameraImageData.primary.image_data.fps < CAMERA_STANDARD_FPS_RANGE.min ||
						cameraImageData.primary.image_data.fps > CAMERA_STANDARD_FPS_RANGE.max
					) {
						handleOpenFPSConfirmationDialog();
					} else {
						setCurrentStep(1);
					}
				}
			} else {
				dispatch(
					showFeedbackNotification({
						message: "Image not found",
						severity: "error"
					})
				);
			}
		}
	}, [getCameraImageSuccess, cameraImageData, dispatch, RestrictedAccess]);

	useEffect(() => {
		if (getCameraImageError && "data" in getCameraImageError) {
			dispatch(
				showFeedbackNotification({
					message: getCameraImageError.data ? String(getCameraImageError.data) : "Something went wrong",
					severity: "error"
				})
			);
		}
	}, [dispatch, getCameraImageError]);

	useEffect(() => {
		if (updateCameraSuccess && updateCameraResponse) {
			resetUpdateCameraDetailsState();
			onClose();
		}
	}, [resetUpdateCameraDetailsState, updateCameraSuccess, updateCameraResponse, onClose]);

	useEffect(() => {
		if (addCameraSuccess) {
			resetAddCameraState();
			refetchCamerasList();
			onClose();
		}
	}, [addCameraSuccess, onClose, refetchCamerasList, resetAddCameraState]);

	useEffect(() => {
		if (crossingLinesGeneration === "auto") {
			setNumberOfLines(String(DEFAULT_NUMBER_OF_LINES));
		}
	}, [crossingLinesGeneration]);

	useEffect(() => {
		if (updateCameraError) {
			dispatch(
				showFeedbackNotification({
					message:
						"data" in updateCameraError && updateCameraError.data
							? String(updateCameraError.data)
							: "Failed to update camera details. Please try again.",
					severity: "error"
				})
			);

			resetUpdateCameraDetailsState();
		}
	}, [dispatch, resetUpdateCameraDetailsState, updateCameraError]);

	useEffect(() => {
		if (addCameraError) {
			dispatch(
				showFeedbackNotification({
					message:
						"data" in addCameraError && addCameraError.data
							? String(addCameraError.data)
							: "Failed to add camera. Please try again.",
					severity: "error"
				})
			);

			resetAddCameraState();
		}
	}, [addCameraError, dispatch, resetAddCameraState]);

	const RenderCanvasStep: { [step: number]: ReactNode } = {
		0: (
			<Box>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Box className="camera-location-selector-wrapper">
							<Typography fontWeight={600} color="var(--color-primary-main)">
								Camera Direction
							</Typography>

							<FormControl fullWidth>
								<Controller
									name="camera_location"
									control={control}
									defaultValue="1"
									rules={{
										required: "Camera direction is required",
										onChange: handleCameraLocationChange
									}}
									render={({ field: { value, onChange } }) => (
										<RadioGroup row sx={{ gap: "2rem" }}>
											<FormControlLabel
												value={1}
												control={<Radio />}
												label="Entry"
												checked={Number(value) === 1}
												onChange={(_, checked) => onChange(checked ? 1 : 0)}
											/>

											<FormControlLabel
												value={0}
												control={<Radio />}
												label="Exit"
												checked={Number(value) === 0}
												onChange={(_, checked) => onChange(checked ? 0 : 1)}
											/>
										</RadioGroup>
									)}
								/>

								{errors.camera_location && errors.camera_location.message ? (
									<FormHelperText error>{errors.camera_location.message.toString()}</FormHelperText>
								) : null}
							</FormControl>
						</Box>
					</Grid>

					<Grid item xs={12} md={4}>
						<InputLabel
							required
							htmlFor="camera-name-input"
							error={!!errors.camera_name}
							sx={{ fontWeight: 500, marginBottom: "0.5rem" }}
						>
							Camera Name
						</InputLabel>

						<TextField
							required
							fullWidth
							size="small"
							variant="outlined"
							color="primary"
							id="camera-name-input"
							autoComplete="off"
							error={!!errors.camera_name}
							helperText={errors.camera_name && errors.camera_name.message ? errors.camera_name.message.toString() : ""}
							InputProps={{ readOnly: RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG) }}
							{...register("camera_name", { required: "Camera name is required" })}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<InputLabel
							required
							htmlFor="rtsp-url-input"
							error={!!errors.rtsp_url}
							sx={{ fontWeight: 500, marginBottom: "0.5rem" }}
						>
							RTSP
						</InputLabel>

						<TextField
							required
							fullWidth
							size="small"
							variant="outlined"
							color="primary"
							id="rtsp-url-input"
							autoComplete="off"
							error={!!errors.rtsp_url}
							helperText={errors.rtsp_url && errors.rtsp_url.message ? errors.rtsp_url.message.toString() : ""}
							InputProps={{
								readOnly: RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG),
								endAdornment: (
									<InputAdornment position="end">
										{showCopyRtspButton.rtsp_url ? (
											<IconButton
												size="small"
												onClick={() => handleCopyRTSP("rtsp_url")}
												sx={{
													color: "#5b4ebf",
													"&:hover": {
														background: "rgba(91, 78, 191, 0.08)",
														transform: "scale(1.1)"
													},
													transition: "all 0.2s ease-in-out"
												}}
											>
												<ContentCopyIcon fontSize="small" />
											</IconButton>
										) : (
											<IconButton size="small" disabled>
												<CheckCircleIcon fontSize="small" color="success" />
											</IconButton>
										)}
									</InputAdornment>
								)
							}}
							{...register("rtsp_url", {
								required: "RTSP is required",
								validate: (value) => {
									if (!value) value = "";

									if (value.startsWith("rtsp://")) {
										if (!RTSP_REGEX.test(value)) {
											return "Enter a valid RTSP URL";
										}

										return true;
									}

									if (siteConfig && siteConfig.default_econ_path !== value) {
										return "Enter a valid RTSP URL";
									}

									return true;
								}
							})}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<InputLabel htmlFor="secondary-rtsp-url-input" sx={{ fontWeight: 500, marginBottom: "0.5rem" }}>
							Secondary RTSP
						</InputLabel>

						<TextField
							fullWidth
							size="small"
							variant="outlined"
							color="primary"
							id="secondary-rtsp-url-input"
							autoComplete="off"
							error={!!errors.secondary_rtsp_url}
							helperText={
								errors.secondary_rtsp_url && errors.secondary_rtsp_url.message
									? errors.secondary_rtsp_url.message.toString()
									: ""
							}
							InputProps={{
								readOnly: RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG),
								endAdornment: (
									<InputAdornment position="end">
										{showCopyRtspButton.secondary_rtsp_url ? (
											<IconButton
												size="small"
												onClick={() => handleCopyRTSP("secondary_rtsp_url")}
												sx={{
													color: "#5b4ebf",
													"&:hover": {
														background: "rgba(91, 78, 191, 0.08)",
														transform: "scale(1.1)"
													},
													transition: "all 0.2s ease-in-out"
												}}
											>
												<ContentCopyIcon fontSize="small" />
											</IconButton>
										) : (
											<IconButton size="small" disabled>
												<CheckCircleIcon fontSize="small" color="success" />
											</IconButton>
										)}
									</InputAdornment>
								)
							}}
							{...register("secondary_rtsp_url", {
								validate: (value) => {
									if (value) {
										if (value.startsWith("rtsp://")) {
											if (!RTSP_REGEX.test(value)) {
												return "Enter a valid RTSP URL";
											}

											return true;
										}

										if (siteConfig && siteConfig.default_econ_path !== value) {
											return "Enter a valid RTSP URL";
										}
									}

									return true;
								}
							})}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<InputLabel
							required
							htmlFor="site-id-input"
							error={!!errors.site_id}
							sx={{ fontWeight: 500, marginBottom: "0.5rem" }}
						>
							Site ID
						</InputLabel>

						<NumberInput
							required
							fullWidth
							positiveOnly
							disableSigned
							disableDecimal
							size="small"
							variant="outlined"
							color="primary"
							id="site-id-input"
							error={!!errors.site_id}
							helperText={errors.site_id && errors.site_id.message ? errors.site_id.message.toString() : ""}
							InputProps={{ readOnly: RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG) }}
							{...register("site_id", {
								required: "Site ID is required",
								min: { value: 0, message: "Site ID cannot be negative" },
								validate: (value) => {
									const existingSiteIdCamera = camerasList.find(
										(cameraItem) => Number(cameraItem.site_id) === Number(value)
									);

									if (
										existingSiteIdCamera &&
										existingSiteIdCamera.id !== cameraDetails?.id &&
										existingSiteIdCamera.is_entry !== Boolean(Number(getValues("camera_location")))
									) {
										return `A camera with this site ID already exists for ${
											existingSiteIdCamera.is_entry ? "entry" : "exit"
										}`;
									}

									return true;
								},
								onChange: () => trigger("site_id")
							})}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<InputLabel
							required
							htmlFor="source-id-input"
							error={!!errors.camera_id}
							sx={{ fontWeight: 500, marginBottom: "0.5rem" }}
						>
							Camera ID
						</InputLabel>

						<NumberInput
							required
							fullWidth
							positiveOnly
							disableSigned
							disableDecimal
							size="small"
							variant="outlined"
							color="primary"
							id="source-id-input"
							error={!!errors.camera_id}
							helperText={errors.camera_id && errors.camera_id.message ? errors.camera_id.message.toString() : ""}
							InputProps={{ readOnly: RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG) }}
							{...register("camera_id", {
								required: "Camera ID is required",
								min: { value: 0, message: "Camera ID cannot be negative" },
								validate: (value) => {
									const existingSourceIdCamera = camerasList.find(
										(cameraItem) => Number(cameraItem.camera_id) === Number(value)
									);

									if (existingSourceIdCamera && existingSourceIdCamera.id !== cameraDetails?.id) {
										return "A camera with this source ID already exists";
									}

									return true;
								},
								onChange: () => trigger("camera_id")
							})}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<InputLabel htmlFor="camera-settings-url-input" sx={{ fontWeight: 500, marginBottom: "0.5rem" }}>
							Camera Settings URL
						</InputLabel>

						<TextField
							required
							fullWidth
							size="small"
							variant="outlined"
							color="primary"
							id="camera-settings-url-input"
							autoComplete="off"
							error={!!errors.camera_settings_url}
							helperText={
								errors.camera_settings_url && errors.camera_settings_url.message
									? errors.camera_settings_url.message.toString()
									: ""
							}
							InputProps={{ readOnly: RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG) }}
							{...register("camera_settings_url", { pattern: { value: URL_REGEX, message: "Enter a valid URL" } })}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<InputLabel htmlFor="tcp-socket-host-input" sx={{ fontWeight: 500, marginBottom: "0.5rem" }}>
							TCP Socket Host
						</InputLabel>

						<TextField
							fullWidth
							size="small"
							variant="outlined"
							color="primary"
							id="tcp-socket-host-input"
							autoComplete="off"
							error={!!errors.tcp_socket_host}
							helperText={
								errors.tcp_socket_host && errors.tcp_socket_host.message
									? errors.tcp_socket_host.message.toString()
									: ""
							}
							InputProps={{ readOnly: RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG) }}
							{...register("tcp_socket_host")}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<InputLabel htmlFor="tcp-socket-port-input" sx={{ fontWeight: 500, marginBottom: "0.5rem" }}>
							TCP Socket Port
						</InputLabel>

						<TextField
							fullWidth
							size="small"
							variant="outlined"
							color="primary"
							id="tcp-socket-port-input"
							autoComplete="off"
							error={!!errors.tcp_socket_port}
							helperText={
								errors.tcp_socket_port && errors.tcp_socket_port.message
									? errors.tcp_socket_port.message.toString()
									: ""
							}
							InputProps={{ readOnly: RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG) }}
							{...register("tcp_socket_port")}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<InputLabel htmlFor="drop-frame-input" sx={{ fontWeight: 500, marginBottom: "0.5rem" }}>
							Drop Frame
						</InputLabel>

						<NumberInput
							fullWidth
							positiveOnly
							disableSigned
							disableDecimal
							size="small"
							variant="outlined"
							color="primary"
							id="drop-frame-input"
							error={!!errors.drop_frame}
							helperText={errors.drop_frame && errors.drop_frame.message ? errors.drop_frame.message.toString() : ""}
							InputProps={{ readOnly: RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG) }}
							{...register("drop_frame", {
								min: { value: 0, message: "Drop frame cannot be negative" }
							})}
						/>
					</Grid>
				</Grid>
			</Box>
		),
		1: (
			<Box>
				<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
					<Typography variant="h6" color="var(--color-primary-main)">
						Select Region of Interest
					</Typography>

					<Typography variant="body2" color="#616161">
						Plot the dot on the stream to select the region of interest
					</Typography>
				</Box>

				<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxWidth: "100px", marginTop: "1.5rem" }}>
					{showCanvasPreview ? (
						<Button variant="contained" color="primary" startIcon={<EditIcon />} onClick={handleEditButtonClick}>
							Edit
						</Button>
					) : (
						<Button variant="contained" color="primary" startIcon={<RestartAltIcon />} onClick={handleUndoButtonClick}>
							Undo
						</Button>
					)}

					<Button variant="outlined" color="primary" startIcon={<CloseIcon />} onClick={handleResetButtonClick}>
						Reset
					</Button>
				</Box>
			</Box>
		),
		2: (
			<Box>
				<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
					<Typography variant="h6" color="var(--color-primary-main)">
						Define Direction Arrow
					</Typography>

					<Typography variant="body2" color="#616161">
						Draw an arrow in the way where vehicles will approach the camera
					</Typography>
				</Box>

				<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxWidth: "100px", marginTop: "1.5rem" }}>
					{showCanvasPreview ? (
						<Button variant="contained" color="primary" startIcon={<EditIcon />} onClick={handleEditButtonClick}>
							Edit
						</Button>
					) : (
						<Button variant="contained" color="primary" startIcon={<RestartAltIcon />} onClick={handleUndoButtonClick}>
							Undo
						</Button>
					)}

					<Button variant="outlined" color="primary" startIcon={<CloseIcon />} onClick={handleResetButtonClick}>
						Reset
					</Button>
				</Box>
			</Box>
		),
		3: (
			<Box>
				<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
					<Typography variant="h6" color="var(--color-primary-main)">
						Setup Crossing Lines
					</Typography>

					<Typography variant="body2" color="#616161">
						Setup the crossing lines (each time a vehicle crosses the line the number plate is read)
					</Typography>
				</Box>

				<FormControl fullWidth sx={{ marginTop: "1rem" }}>
					<RadioGroup>
						<FormControlLabel
							value="auto"
							control={<Radio />}
							label="Automatic"
							disabled={showCanvasPreview}
							checked={crossingLinesGeneration === "auto"}
							onChange={(event) =>
								handleChangeCrossingLinesGeneration((event.target as HTMLInputElement).value as "auto" | "manual")
							}
						/>

						<FormControlLabel
							value="manual"
							control={<Radio />}
							label="Manual"
							disabled={showCanvasPreview}
							checked={crossingLinesGeneration === "manual"}
							onChange={(event) =>
								handleChangeCrossingLinesGeneration((event.target as HTMLInputElement).value as "auto" | "manual")
							}
						/>

						<NumberInput
							positiveOnly
							disableSigned
							disableDecimal
							autoComplete="new-password"
							variant="outlined"
							size="small"
							placeholder="Add number of lines"
							disabled={crossingLinesGeneration !== "manual" || showCanvasPreview}
							value={numberOfLines}
							onChange={(event) => handleChangeNumberOfLines(event.target.value)}
						/>
					</RadioGroup>
				</FormControl>

				<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxWidth: "100px", marginTop: "1.5rem" }}>
					{showCanvasPreview ? (
						<Button variant="contained" color="primary" startIcon={<EditIcon />} onClick={handleEditButtonClick}>
							Edit
						</Button>
					) : (
						<Button variant="contained" color="primary" startIcon={<RestartAltIcon />} onClick={handleUndoButtonClick}>
							Undo
						</Button>
					)}

					<Button variant="outlined" color="primary" startIcon={<CloseIcon />} onClick={handleResetButtonClick}>
						Reset
					</Button>
				</Box>
			</Box>
		)
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
			<DialogTitle
				textAlign="center"
				sx={{
					background: "#5b4ebf",
					color: "white",
					position: "relative",
					padding: "16px 20px 12px 20px",
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
					<Typography variant="h6" sx={{ fontWeight: 600 }}>
						{RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG) ? "Camera Setup" : "Update Camera Setup"}
					</Typography>
				</Box>

				<Box sx={{ position: "absolute", top: 12, right: 12 }}>
					<IconButton
						onClick={onClose}
						sx={{
							color: "white",
							background: "rgba(255, 255, 255, 0.1)",
							backdropFilter: "blur(8px)",
							borderRadius: "50%",
							padding: "6px",
							"&:hover": {
								background: "rgba(255, 255, 255, 0.2)",
								transform: "scale(1.1)"
							},
							transition: "all 0.2s ease-in-out"
						}}
					>
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>

			{RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG) ? (
				<DialogContent
					sx={{
						padding: "2rem !important",
						background: "#ffffff"
					}}
				>
					<Box sx={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
						{showReadOnlyPreview ? (
							<Box
								sx={{
									width: "80%",
									height: "100%",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									alignSelf: "center"
								}}
							>
								{(imageDetails && imageDetails.image && imageRTSP === rtspUrlInput) ||
								(cameraImageData &&
									cameraImageData.primary &&
									cameraImageData.primary.image_data &&
									cameraImageData.primary.image_data.image) ? (
									<CanvasComponent
										ref={canvasRef}
										image={
											imageDetails && imageRTSP === rtspUrlInput
												? imageDetails.image
												: cameraImageData && cameraImageData.primary && cameraImageData.primary.image_data
												? cameraImageData.primary.image_data.image
												: ""
										}
										// width={cameraImageData.primary.image_data.width}
										// height={cameraImageData.primary.image_data.height}
										numberOfLines={Number(numberOfLines)}
										cameraDetails={cameraDetails}
										rtspUrlInput={rtspUrlInput}
										onSaveDetails={handleSubmitCameraSetupDetails}
										setCurrentCanvasStep={setCurrentStep}
										setCrossingLinesGeneration={setCrossingLinesGeneration}
										setShowCanvasPreview={setShowCanvasPreview}
										setShowPreviewLoader={setShowPreviewLoader}
										setShowSubmitButton={setShowSubmitButton}
									/>
								) : getCameraImageLoading || getCameraImageFetching ? (
									<CircularProgress />
								) : null}
							</Box>
						) : (
							RenderCanvasStep[0]
						)}

						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
							{showReadOnlyPreview ? (
								<LoadingButton
									variant="contained"
									color="primary"
									onClick={handleShowCameraDetails}
									loading={isNextButtonLoading()}
								>
									Camera Details
								</LoadingButton>
							) : (
								<LoadingButton
									variant="contained"
									color="primary"
									onClick={handleNextButtonClick}
									loading={isNextButtonLoading()}
								>
									ROI Setup
								</LoadingButton>
							)}
						</Box>
					</Box>
				</DialogContent>
			) : (
				<DialogContent
					sx={{
						padding: "2rem !important",
						background: "#ffffff"
					}}
				>
					<Stepper
						alternativeLabel
						activeStep={currentStep}
						connector={
							<StepConnector
								classes={{
									line: "camera-setup-stepper-connector-line",
									root: "camera-setup-stepper-connector-root",
									completed: "camera-setup-stepper-connector-completed",
									active: "camera-setup-stepper-connector-completed"
								}}
							/>
						}
					>
						<Step classes={{ root: "camera-setup-step-root" }}>
							<StepLabel
								classes={{
									iconContainer: "camera-setup-step-label-icon-container",
									disabled: "camera-setup-step-label-disabled"
								}}
							>
								Setup Camera Details
							</StepLabel>
						</Step>

						{CAMERA_SETUP_STEPS.map((stepItem) => (
							<Step key={stepItem.id} classes={{ root: "camera-setup-step-root" }}>
								<StepLabel
									classes={{
										iconContainer: "camera-setup-step-label-icon-container",
										disabled: "camera-setup-step-label-disabled"
									}}
								>
									{stepItem.label}
								</StepLabel>
							</Step>
						))}
					</Stepper>

					<Grid container spacing={2} sx={{ marginTop: "1rem" }}>
						{currentStep >= 1 ? (
							<Grid item xs={12} md={8}>
								<Box
									sx={{
										width: "100%",
										height: "100%",
										display: "flex",
										alignItems: "center",
										justifyContent: "center"
									}}
								>
									{(imageDetails && imageDetails.image && imageRTSP === rtspUrlInput) ||
									(cameraImageData &&
										cameraImageData.primary &&
										cameraImageData.primary.image_data &&
										cameraImageData.primary.image_data.image) ? (
										<CanvasComponent
											ref={canvasRef}
											image={
												imageDetails && imageRTSP === rtspUrlInput
													? imageDetails.image
													: cameraImageData && cameraImageData.primary && cameraImageData.primary.image_data
													? cameraImageData.primary.image_data.image
													: ""
											}
											// width={cameraImageData.primary.image_data.width}
											// height={cameraImageData.primary.image_data.height}
											numberOfLines={Number(numberOfLines)}
											cameraDetails={cameraDetails}
											rtspUrlInput={rtspUrlInput}
											onSaveDetails={handleSubmitCameraSetupDetails}
											setCurrentCanvasStep={setCurrentStep}
											setCrossingLinesGeneration={setCrossingLinesGeneration}
											setShowCanvasPreview={setShowCanvasPreview}
											setShowPreviewLoader={setShowPreviewLoader}
											setShowSubmitButton={setShowSubmitButton}
										/>
									) : getCameraImageLoading || getCameraImageFetching ? (
										<CircularProgress />
									) : null}
								</Box>
							</Grid>
						) : null}

						<Grid item xs={12} md={currentStep < 1 ? 12 : 4}>
							<Box sx={{ display: "flex", flexDirection: "column", gap: "2rem" }}>{RenderCanvasStep[currentStep]}</Box>
						</Grid>

						<Grid item xs={12}>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "1rem" }}>
								{currentStep > 0 ? (
									<Button
										variant="outlined"
										color="primary"
										onClick={handleBackButtonClick}
										startIcon={<ChevronLeftIcon />}
									>
										Back
									</Button>
								) : null}

								{cameraDetails && currentStep === 0 ? (
									<LoadingButton
										variant="contained"
										color="success"
										startIcon={<SaveIcon />}
										loading={updateCameraLoading || addCameraLoading}
										loadingPosition="start"
										onClick={handleSaveButtonClick}
										disabled={isSaveButtonDisabled()}
									>
										Save
									</LoadingButton>
								) : null}

								{currentStep < CAMERA_SETUP_STEPS.length ? (
									<LoadingButton
										variant="contained"
										color="primary"
										onClick={handleNextButtonClick}
										endIcon={<ChevronRightIcon />}
										loading={isNextButtonLoading()}
										loadingPosition="end"
									>
										Next
									</LoadingButton>
								) : showSubmitButton ? (
									<LoadingButton
										variant="contained"
										color="success"
										startIcon={<SaveIcon />}
										loading={updateCameraLoading || addCameraLoading}
										loadingPosition="start"
										onClick={handleSaveButtonClick}
									>
										Save
									</LoadingButton>
								) : (
									<LoadingButton
										variant="contained"
										color="primary"
										startIcon={<ArrowRightIcon />}
										onClick={handlePreviewButtonClick}
										loading={showPreviewLoading}
										loadingPosition="start"
									>
										Preview
									</LoadingButton>
								)}
							</Box>
						</Grid>
					</Grid>
				</DialogContent>
			)}

			{/* CAMERA NON-STANDARD RESOLUTION CONFIRMATION DIALOG */}
			<ConfirmationDialog
				width="sm"
				color="primary"
				title="Confirm Camera Resolution"
				heading={`Adding a camera that is not ${CAMERA_STANDARD_RESOLUTION.width} x ${CAMERA_STANDARD_RESOLUTION.height}. Are you sure you want to proceed?`}
				open={showResolutionConfirmation}
				onClose={handleCloseResolutionConfirmationDialog}
				onConfirm={handleConfirmCameraResolution}
			/>

			{/* CAMERA NON-STANDARD FPS CONFIRMATION DIALOG */}
			<ConfirmationDialog
				width="sm"
				color="primary"
				title="Confirm Camera FPS"
				heading={`Adding a camera that is not ${CAMERA_STANDARD_FPS_RANGE.min}-${CAMERA_STANDARD_FPS_RANGE.max} FPS. Are you sure you want to proceed?`}
				open={showFPSConfirmation}
				onClose={handleCloseFPSConfirmationDialog}
				onConfirm={handleConfirmCameraFPS}
			/>
		</Dialog>
	);
};

export default CameraSetupDialog;
